import { useEffect, useRef } from 'react';
import { KeybindingManager } from './manager';
import { defaultKeybindings } from './defaults';
import { useEditorStore } from '@/store/editorStore';
import { useDocumentStore } from '@/store/documentStore';
import type { CommandContext } from '@/commands/types';
import { isModKey } from '@/utils/platform';

/** Keys that the browser uses for native zoom -- we intercept these. */
const BROWSER_ZOOM_KEYS = new Set(['=', '+', '-', '0']);

export function useKeybindings(): void {
  const managerRef = useRef<KeybindingManager | null>(null);

  useEffect(() => {
    const getContext = (): CommandContext => ({
      doc: useDocumentStore.getState().mapDoc,
      editorState: useEditorStore.getState(),
    });

    const getMode = (): string => useEditorStore.getState().mode;

    const manager = new KeybindingManager(defaultKeybindings, getContext, getMode);
    managerRef.current = manager;

    const detach = manager.attach(window);

    // Capture-phase listener to prevent browser native zoom (Ctrl+=/Ctrl+-/Ctrl+0)
    // This runs before the browser's built-in handler so preventDefault() works.
    function preventBrowserZoom(e: KeyboardEvent) {
      if (isModKey(e) && BROWSER_ZOOM_KEYS.has(e.key)) {
        e.preventDefault();
      }
    }
    window.addEventListener('keydown', preventBrowserZoom, { capture: true });

    return () => {
      detach();
      window.removeEventListener('keydown', preventBrowserZoom, { capture: true });
    };
  }, []);
}
